import { JSX } from 'react';
import { AccessConfig, LogisticTeamAccess, ReportAccess } from '../../../types/user';
import { getAccessIcon } from '../../../utils/iconUtils';
import { translate } from '../../../../common/language/translations';
import { AccessTableOptionCell, AccessTableToggleCell } from '../tableCells';
import { EventPropertiesMap } from '../../../services/server/analytics/events';
import { InfoOutlined } from '@mui/icons-material';
import { IconButton } from '../../controls/buttons/IconButton';


interface AccessTableColumnHeaderProps {
  openTextModal: (textTitle: string, text: string, eventProperties?: EventPropertiesMap['InfoModalOpened']) => void;
  language?: string;
}


interface AccessTableColumnCellProps {
  row: AccessConfig;
  setCurrentCustomAccessConfig?: (accessConfig: AccessConfig) => void;
  handleOpenAccountTableOptionModal?: (accountTableOptionModalType?: 'logisticTeamAccess' | 'reportAccess') => void;
  language?: string;
}


interface AccessTableColumn {
  key: string;
  width: number;
  isStickyColumn?: boolean;
  header: (props: AccessTableColumnHeaderProps) => JSX.Element;
  cell: (props: AccessTableColumnCellProps) => JSX.Element;
}


export const accessTableStickyColumnWidth = 160;
const accessTableNonStickyColumnWidth = 80;
export const accessTableColumnsWidth = accessTableNonStickyColumnWidth * 12;
export const accessTableColumns: AccessTableColumn[] = [
  {
    key: 'accessType',
    width: accessTableStickyColumnWidth,
    isStickyColumn: true,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell' style={{ justifyContent: 'left' }}>
        <IconButton
          icon={<InfoOutlined style={{ fontSize: 14 }} />}
          onClick={() => openTextModal(
            translate('accesses', language),
            translate('accessesAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'accesses' }
          )}
          size={19}
          style={{ marginLeft: 7, marginRight: 5 }}
          startWhite={true}
        />
        {translate('accesses', language)}
      </div>
    ),
    cell: ({ row, language }: AccessTableColumnCellProps) => (
      <div className='table-cell' style={{ justifyContent: 'left' }}>
        <div style={{ marginLeft: 9, marginRight: 7 }}>
          {getAccessIcon(row.accessType, 18)}
        </div>
        {translate(row.accessType + 'Access', language)}
      </div>
    ),
  },
  {
    key: 'hasAccessToPlatform',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('platform', language),
            translate('platformAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'platform' }
          )}
        >
          {translate('platform', language)}
        </div>
      </div>
    ),
    cell: ({ row }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.accessType !== 'none'}
      />
    ),
  },
  {
    key: 'hasAccessToManageUsers',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('users', language),
            translate('usersAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'manageUsers' }
          )}
        >
          {translate('users', language)}
        </div>
      </div>
    ),
    cell: ({ row }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.accessType === 'admin'}
      />
    ),
  },
  {
    key: 'logisticTeamAccess',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('logisticTeams', language),
            translate('logisticTeamAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'logisticTeams' }
          )}
        >
          {translate('logisticTeams', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig, language }: AccessTableColumnCellProps) => (
      <div className='table-cell'>
        <AccessTableOptionCell
          row={row}
          property={'logisticTeamAccess'}
          handleOptionClick={
            setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, logisticTeamAccess: value as LogisticTeamAccess }) : undefined
          }
          language={language}
        />
      </div>
    ),
  },
  {
    key: 'hasAccessToScouting',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('scouting', language),
            translate('scoutingAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'scouting' }
          )}
        >
          {translate('scouting', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.hasAccessToScouting}
        setIsToggled={setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, hasAccessToScouting: value }) : undefined}
      />
    ),
  },
  {
    key: 'reportAccess',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('reports', language),
            translate('reportAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'reports' }
          )}
        >
          {translate('reports', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig, language }: AccessTableColumnCellProps) => (
      <div className='table-cell'>
        <AccessTableOptionCell
          row={row}
          property={'reportAccess'}
          handleOptionClick={
            setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, reportAccess: value as ReportAccess }) : undefined
          }
          language={language}
        />
      </div>
    ),
  },
  {
    key: 'hasAccessToModifyOwnTeam',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('editOwnTeam', language),
            translate('editOwnTeamAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'editOwnTeam' }
          )}
        >
          {translate('editOwnTeam', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.hasAccessToModifyOwnTeam}
        setIsToggled={setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, hasAccessToModifyOwnTeam: value }) : undefined}
      />
    ),
  },
  {
    key: 'hasAccessToViewTeamRoles',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('teamRoles', language),
            translate('teamRolesAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'teamRoles' }
          )}
        >
          {translate('teamRoles', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.hasAccessToViewTeamRoles}
        setIsToggled={setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, hasAccessToViewTeamRoles: value }) : undefined}
      />
    ),
  },
  {
    key: 'hasAccessToPlanner',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('planner', language),
            translate('plannerAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'planner' }
          )}
        >
          {translate('planner', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.hasAccessToPlanner}
        setIsToggled={setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, hasAccessToPlanner: value }) : undefined}
      />
    ),
  },
  {
    key: 'hasAccessToEconomy',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('economy', language),
            translate('economyAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'economy' }
          )}
        >
          {translate('economy', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.hasAccessToEconomy}
        setIsToggled={setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, hasAccessToEconomy: value }) : undefined}
      />
    ),
  },
  {
    key: 'hasAccessToAcademy',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('academy', language),
            translate('academyAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'academy' }
          )}
        >
          {translate('academy', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.hasAccessToAcademy}
        setIsToggled={setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, hasAccessToAcademy: value }) : undefined}
      />
    ),
  },
  {
    key: 'hasAccessToManageFormations',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('editFormations', language),
            translate('editFormationsAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'editFormations' }
          )}
        >
          {translate('editFormations', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.hasAccessToManageFormations}
        setIsToggled={setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, hasAccessToManageFormations: value }) : undefined}
      />
    ),
  },
  {
    key: 'hasAccessToManageRoles',
    width: accessTableNonStickyColumnWidth,
    header: ({ openTextModal, language }: AccessTableColumnHeaderProps) => (
      <div className='table-cell'>
        <div
          className='table-header-cell-clickable'
          style={{ padding: '0px 6px', textAlign: 'center' }}
          onClick={() => openTextModal(
            translate('editRoles', language),
            translate('editRolesAccessInfo', language),
            { infoType: 'infoIcon', titleKey: 'editRoles' }
          )}
        >
          {translate('editRoles', language)}
        </div>
      </div>
    ),
    cell: ({ row, setCurrentCustomAccessConfig }: AccessTableColumnCellProps) => (
      <AccessTableToggleCell
        isToggled={row.hasAccessToManageRoles}
        setIsToggled={setCurrentCustomAccessConfig ? (value) => setCurrentCustomAccessConfig({ ...row, hasAccessToManageRoles: value }) : undefined}
      />
    ),
  },
];
